/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    Box,
    Typography,
    Button,
    Chip,
    CircularProgress,
    TextField,
    InputAdornment,
    Avatar,
    alpha,
    IconButton,
    Tooltip,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Link,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { Transition } from 'src/components/dialogPopUp';
import Iconify from 'src/components/Iconify';
import { DATE_TITLES } from 'src/constants/newLead';
import { getLead } from 'src/redux/slices/analytics';
import { parseApiDate } from 'src/redux/slices/analyticsReports';
import { STATUS_COLORS, STATUS_CONFIG } from 'src/constants/reportStatusPalette';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import { getReportLeadContactCount } from 'src/utils/reportLead';
import { dispatch, useSelector } from 'src/redux/store';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

// STATUS_COLORS + STATUS_CONFIG now come from the shared palette module
// so TreatmentLeadPopup and SourceLeadPopup can't drift apart.

export default function SourceLeadPopup({
    open,
    onClose,
    practiceId,
    sourceType,
    referral,
    status,
    statusLabel,
    dateRange,
    title: titleProp,
    treatment,
    assignedTo,
    isArchived,
    dateFilterMode,
    archivedReason,
    categoryId,
    leadIds,
}) {
    const buildExportName = useReportExportFilename();
    const [searchQuery, setSearchQuery] = useState('');
    // Default page size is 10 rows with no internal scrollbar — the dialog itself
    // is tall enough (see `rowHeight` + `autoHeight` on the grid) to fit all 10.
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [sortModel, setSortModel] = useState([{ field: 'created_at', sort: 'desc' }]);
    const [dateFormat, setDateFormat] = useState(() => localStorage.getItem('reportsPopupDateFormat') || 'human_readable');
    const [dateFormatAnchor, setDateFormatAnchor] = useState(null);

    const { leads, reportsLoading } = useSelector((state) => state.analytics || {});
    const leadData = leads?.results || [];
    const totalCount = leads?.count || 0;

    const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.All;

    // Fetch leads when filters change
    const fetchLeads = useCallback(() => {
        if (!practiceId || !open) return;

        const orderBy = sortModel[0]?.field || 'created_at';
        const order = sortModel[0]?.sort || 'desc';

        dispatch(
            getLead({
                id: practiceId,
                status: status === 'All' ? null : status,
                limit: paginationModel.pageSize,
                offset: paginationModel.page * paginationModel.pageSize,
                order,
                orderId: orderBy,
                startDate: dateRange?.startDate ? dayjs(dateRange.startDate).format('YYYY-MM-DD') : undefined,
                endDate: dateRange?.endDate ? dayjs(dateRange.endDate).format('YYYY-MM-DD') : undefined,
                source: sourceType ? [sourceType] : undefined,
                referral: referral ? [referral] : undefined,
                treatment: treatment || undefined,
                assignedTo: assignedTo || undefined,
                isArchived: isArchived || undefined,
                useCreatedAt: dateFilterMode === 'created_at',
                archivedReason: archivedReason || undefined,
                categoryId: categoryId || undefined,
                leadIds: leadIds?.length ? leadIds : undefined,
            })
        );
    }, [practiceId, open, status, dateRange, paginationModel, sortModel, sourceType, referral, treatment, assignedTo, isArchived, dateFilterMode, archivedReason, categoryId, leadIds]);

    useEffect(() => {
        if (open) {
            fetchLeads();
        }
    }, [fetchLeads, open]);

    // Get treatment name from lead data
    const getTreatmentName = (lead) => {
        const treatments = lead?.lead_treatments || [];
        if (treatments.length === 0) return '-';
        const names = treatments.map((t) => (typeof t?.treatment === 'object' ? t?.treatment?.name : t?.treatment) || t?.treatment_name || 'Unknown').slice(0, 2);
        return names.length > 2 ? `${names.join(', ')} +${treatments.length - 2}` : names.join(', ');
    };

    // Get treatment value from lead data
    const getTreatmentValue = (lead) => {
        const treatments = lead?.lead_treatments || [];
        const total = treatments.reduce((sum, t) => sum + (t?.custom_price || 0), 0);
        return total > 0 ? `£${total.toLocaleString()}` : '-';
    };

    // Get lead status
    const getLeadStatus = (lead) => {
        const treatments = lead?.lead_treatments || [];
        if (treatments.length === 0) return 'NEW';
        return treatments[0]?.status || 'NEW';
    };

    // Delegates to the single source of truth in analyticsReports.js —
    // handles DD-MM-YYYY with microseconds correctly and in UTC.
    const parseDate = (dateStr) => parseApiDate(dateStr);

    // Format date based on current dateFormat mode
    const formatDate = (dateStr, humanReadable) => {
        if (dateFormat === 'human_readable') {
            if (humanReadable) return humanReadable;
            const d = parseDate(dateStr);
            return d ? d.fromNow() : '-';
        }
        const d = parseDate(dateStr);
        if (!d) return '-';
        if (dateFormat === 'date_time_format') return d.format('DD MMM YYYY, h:mm A');
        return d.format('DD MMM YYYY');
    };

    // Handle date format toggle
    const handleDateFormatChange = (value) => {
        setDateFormat(value);
        localStorage.setItem('reportsPopupDateFormat', value);
        setDateFormatAnchor(null);
    };

    // Returns both halves so the cell can render referral as the primary label
    // and source type as a small grey subtitle. Most data lives on referral;
    // source type is the generic channel fallback.
    const getLeadSource = (lead) => {
        const referralName = lead?.lead_source?.referral || '';
        const sourceTypeName = lead?.lead_source?.type || '';
        const primary = referralName || sourceTypeName || '-';
        const secondary = referralName && sourceTypeName && referralName !== sourceTypeName
            ? sourceTypeName
            : '';
        return { primary, secondary };
    };

    // Process lead data with calculated fields
    const processedData = useMemo(() =>
        leadData.map((lead) => {
            const leadSource = getLeadSource(lead);
            return {
                id: lead.id,
                name: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
                firstName: lead.first_name,
                lastName: lead.last_name,
                treatment: getTreatmentName(lead),
                status: getLeadStatus(lead),
                value: getTreatmentValue(lead),
                source: leadSource.primary,
                sourceSecondary: leadSource.secondary,
                timesContacted: getReportLeadContactCount(lead),
                created_at: lead.created_at,
                created_at_human: lead.created_at_human,
            };
        }),
    [leadData]);

    // Filter by search query (client-side)
    const filteredData = useMemo(() => {
        if (!searchQuery) return processedData;
        const query = searchQuery.toLowerCase();
        return processedData.filter((row) =>
            row.name.toLowerCase().includes(query) ||
            row.treatment.toLowerCase().includes(query)
        );
    }, [processedData, searchQuery]);

    // Export CSV
    const handleExportCSV = useCallback(() => {
        if (!filteredData || filteredData.length === 0) return;
        // Mirror the 7 columns the grid renders. The single "Source" column shows
        // a primary line + optional secondary line; fold both into one cell.
        const headers = ['Name', 'Treatment', 'Status', 'Source', 'Times contacted', 'Value', 'Date'];
        const csvRows = filteredData.map((row) => [
            row.name,
            row.treatment,
            STATUS_COLORS[row.status]?.label || row.status,
            row.sourceSecondary ? `${row.source || ''} (${row.sourceSecondary})` : (row.source || ''),
            row.timesContacted || 0,
            row.value,
            formatDate(row.created_at),
        ]);
        const csvContent = [headers, ...csvRows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = buildExportName(`leads-${(sourceType || referral || 'all').toLowerCase()}`, dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [filteredData, sourceType, referral, dateRange, buildExportName]);

    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1.5,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 1 }}>
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                            fontSize: '12px',
                            fontWeight: 600,
                            bgcolor: alpha('#3b82f6', 0.1),
                            color: '#3b82f6',
                        }}
                    >
                        {(params.row.firstName?.[0] || '').toUpperCase()}
                        {(params.row.lastName?.[0] || '').toUpperCase()}
                    </Avatar>
                    <Link
                        href={`/dashboard/practice/${practiceId}/lead-detail/${params.row.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontSize: '13px', fontWeight: 500, color: 'primary.main' }}
                    >
                        {params.value}
                    </Link>
                </Box>
            ),
        },
        {
            field: 'treatment',
            headerName: 'Treatment',
            flex: 1.2,
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1.2,
            renderCell: (params) => {
                const statusColorConfig = STATUS_COLORS[params.value] || STATUS_COLORS.NEW;
                return (
                    <Chip
                        label={statusColorConfig.label}
                        size="small"
                        sx={{
                            bgcolor: statusColorConfig.bg,
                            color: statusColorConfig.text,
                            fontWeight: 600,
                            fontSize: '11px',
                            height: 24,
                        }}
                    />
                );
            },
        },
        {
            field: 'source',
            headerName: 'Source',
            flex: 1,
            renderCell: (params) => (
                <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500, lineHeight: 1.4 }}>
                        {params.value}
                    </Typography>
                    {params.row.sourceSecondary && (
                        <Typography sx={{ fontSize: '11px', color: '#64748b', lineHeight: 1.3, mt: 0.25 }}>
                            {params.row.sourceSecondary}
                        </Typography>
                    )}
                </Box>
            ),
        },
        {
            field: 'timesContacted',
            headerName: 'Times contacted',
            flex: 0.7,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontSize: '12px' }}>
                    {params.value || 0}
                </Typography>
            ),
        },
        {
            field: 'value',
            headerName: 'Value',
            flex: 0.8,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#10b981' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'created_at',
            headerName: 'Date',
            flex: 1,
            renderHeader: () => (
                <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setDateFormatAnchor(e.currentTarget);
                    }}
                >
                    <Typography sx={{
                        fontWeight: 600,
                        fontSize: '12px',
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}>
                        {DATE_TITLES.find((o) => o.value === dateFormat)?.label || 'Date'}
                    </Typography>
                    <Iconify icon="mdi:calendar-filter-outline" width={15} height={15} sx={{ color: '#64748b' }} />
                </Box>
            ),
            renderCell: (params) => (
                <Box sx={{ pr: 2 }}>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        {formatDate(params.row.created_at, params.row.created_at_human)}
                    </Typography>
                </Box>
            ),
        },
    ], [dateFormat, practiceId]);

    const handleClose = () => {
        setSearchQuery('');
        setPaginationModel({ page: 0, pageSize: 10 });
        onClose();
    };

    // Build title
    const getTitle = () => {
        if (titleProp) return titleProp;
        if (referral) return `${referral} Leads`;
        if (sourceType) return `${sourceType} Leads`;
        return 'Leads';
    };
    const title = getTitle();

    return (
        <Dialog
            TransitionComponent={Transition}
            open={open}
            onClose={handleClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    height: 'auto',
                    maxHeight: '85vh',
                },
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    p: 2.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                            sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(statusConfig.color, 0.1),
                            }}
                        >
                            <Iconify
                                icon={statusConfig.icon}
                                width={22}
                                height={22}
                                sx={{ color: statusConfig.color }}
                            />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px' }}>
                                {title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                                <Chip
                                    label={statusLabel || statusConfig.label}
                                    size="small"
                                    sx={{
                                        bgcolor: alpha(statusConfig.color, 0.1),
                                        color: statusConfig.color,
                                        fontWeight: 600,
                                        fontSize: '11px',
                                        height: 22,
                                    }}
                                />
                                {dateRange && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                                        {dayjs(dateRange.startDate).format('DD MMM YYYY')} to {dayjs(dateRange.endDate).format('DD MMM YYYY')}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Export CSV" arrow>
                            <IconButton
                                size="small"
                                onClick={handleExportCSV}
                                disabled={!filteredData?.length}
                                sx={{
                                    width: 36,
                                    height: 36,
                                    border: '1px solid',
                                    borderColor: '#e2e8f0',
                                    borderRadius: '8px',
                                    '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                }}
                            >
                                <Iconify icon="eva:download-outline" width={18} sx={{ color: '#64748b' }} />
                            </IconButton>
                        </Tooltip>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClose}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '8px',
                            }}
                        >
                            Close
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Content */}
            {isArchived && (
                <Box sx={{ px: 2.5, pt: 1.5 }}>
                    <Box sx={{ p: 1.5, borderRadius: '8px', bgcolor: alpha('#f59e0b', 0.08), border: `1px solid ${alpha('#f59e0b', 0.3)}`, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Iconify icon="eva:info-outline" width={18} sx={{ color: '#f59e0b', mt: 0.25 }} />
                        <Typography sx={{ fontSize: '12px', color: '#78350f' }}>
                            The archived-lead endpoint does not accept date filters, so this list shows <strong>all archived leads</strong> for the practice rather than only those archived in the selected period. The KPI card count above uses a separate summary endpoint that <em>is</em> period-filtered, so the two numbers may differ until the backend adds server-side date filtering.
                        </Typography>
                    </Box>
                </Box>
            )}
            <Box sx={{ p: 2 }}>
                {/* Search Bar */}
                <Box sx={{ mb: 2 }}>
                    <TextField
                        size="small"
                        placeholder="Search by name or treatment..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: { xs: '100%', sm: 280 },
                            '& .MuiOutlinedInput-root': {
                                bgcolor: '#fff',
                                borderRadius: '8px',
                            },
                        }}
                    />
                </Box>

                {/* DataGrid */}
                {reportsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGridPro
                        rows={filteredData}
                        columns={columns}
                        rowCount={totalCount}
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        sortModel={sortModel}
                        onSortModelChange={setSortModel}
                        sortingMode="server"
                        disableRowSelectionOnClick
                        disableColumnMenu={false}
                        pageSizeOptions={[10, 25, 50]}
                        rowHeight={52}
                        autoHeight
                        columnBuffer={8}
                        // Guarantee 10 rows of real estate even before data loads, so
                        // users never see a cramped 1-row grid with a scrollbar while
                        // the server response is in flight.
                        sx={{
                            minHeight: 52 * 10 + 110, // 10 rows + header + footer
                            border: '1px solid #e2e8f0',
                            borderRadius: '12px',
                            bgcolor: '#fff',
                            '& .MuiDataGrid-virtualScroller': {
                                minHeight: 52 * 10,
                                overflowY: 'visible !important',
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: '#f8fafc',
                                borderBottom: '1px solid #e2e8f0',
                            },
                            '& .MuiDataGrid-columnHeaderTitle': {
                                fontWeight: 600,
                                fontSize: '12px',
                                color: '#64748b',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            },
                            '& .MuiDataGrid-cell': {
                                borderBottom: '1px solid #f1f5f9',
                                py: 1,
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: alpha('#3b82f6', 0.04),
                            },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '1px solid #e2e8f0',
                            },
                        }}
                    />
                )}
            </Box>

            {/* Date format popover */}
            <Popover
                open={Boolean(dateFormatAnchor)}
                anchorEl={dateFormatAnchor}
                onClose={() => setDateFormatAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{
                    paper: {
                        sx: { mt: 0.5, borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.12)', minWidth: 140 },
                    },
                }}
            >
                <List dense sx={{ py: 0.5 }}>
                    {DATE_TITLES.map((option) => (
                        <ListItemButton
                            key={option.value}
                            onClick={() => handleDateFormatChange(option.value)}
                            selected={dateFormat === option.value}
                            sx={{ py: 1, px: 2 }}
                        >
                            <Iconify icon={option.icon} width={18} height={18} sx={{ mr: 1.5, color: dateFormat === option.value ? 'primary.main' : '#64748b' }} />
                            <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: '13px', fontWeight: dateFormat === option.value ? 600 : 400 }} />
                        </ListItemButton>
                    ))}
                </List>
            </Popover>
        </Dialog>
    );
}

SourceLeadPopup.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sourceType: PropTypes.string,
    referral: PropTypes.string,
    status: PropTypes.string,
    statusLabel: PropTypes.string,
    dateRange: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),
    title: PropTypes.string,
    treatment: PropTypes.string,
    assignedTo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isArchived: PropTypes.bool,
    dateFilterMode: PropTypes.string,
    archivedReason: PropTypes.string,
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
};

SourceLeadPopup.defaultProps = {
    practiceId: null,
    sourceType: '',
    referral: '',
    status: 'All',
    statusLabel: '',
    dateRange: null,
    title: '',
    treatment: '',
    assignedTo: null,
    isArchived: false,
    dateFilterMode: 'state_transition',
    archivedReason: '',
    categoryId: null,
    leadIds: [],
};
